import { moduleStatus } from '@/domain/enums/module';
import { assetStates, assetStatesBySeverity } from '@/domain/enums/assetState';

/**
 * Situações intencionais, decididas por alguém. Não são falhas: não participam da
 * consolidação e são contadas à parte. Um Ativo só com elas fica Fora de Operação.
 *
 * Simulação não está aqui: conta como Normal. Ver
 * docs/adr/0008-estado-macro-em-tres-niveis.md
 */
export const NEUTRAL_STATUSES = [
  moduleStatus.DISABLED,
  moduleStatus.UNDER_MAINTENANCE,
];

/**
 * Sensor que não comunica ou comunica mal. Um só basta para o Ativo ficar em
 * Atenção — que reúne o que antes se dividia entre Falha e Intermitente.
 */
export const ATTENTION_STATUSES = [
  moduleStatus.INACTIVE,
  moduleStatus.INDETERMINATE,
  moduleStatus.ESTABLISHING_COMMUNICATION,
  moduleStatus.PARTIAL_COMMUNICATION,
];

/**
 * Compara textos respeitando acentuação e numeração natural, de modo que
 * "TR2" venha antes de "TR10".
 */
function compareNames(left, right) {
  return String(left || '').localeCompare(String(right || ''), 'pt-BR', {
    numeric: true,
    sensitivity: 'base',
  });
}

export function isNeutralStatus(status) {
  return NEUTRAL_STATUSES.indexOf(status) >= 0;
}

/**
 * Identidade do ativo: o `ativoId` que o sensor carrega.
 *
 * Toda derivação de identidade de ativo passa por aqui. Antes de o endpoint
 * expor `ativoId`, a chave era composta por `(instalacaoId, nomeAtivo)`, e dois
 * ativos homônimos na mesma instalação viravam um cartão só.
 * Ver docs/adr/0003-identidade-do-ativo-sem-ativoid.md
 *
 * O backend garante que todo módulo pertence a um ativo. A saída sem `ativoId`
 * existe só para não fundir ativos distintos caso a garantia falhe: cai no id da
 * instância do módulo, que é único, e o sensor órfão aparece sozinho num cartão
 * em vez de contaminar o de outro ativo.
 *
 * @param {Object} module Módulo vindo de `/StatusRede/listagem`.
 * @returns {String} Chave estável dentro de um ciclo de atualização.
 */
export function getAssetKey(module) {
  const { assetId, assetModuleId } = module || {};

  return assetId || assetModuleId || '';
}

/**
 * Consolida os status de comunicação dos sensores no Estado Macro do ativo, por
 * pior-vence, ignorando os estados neutros.
 *
 * @param {Object[]} sensors Sensores do ativo.
 * @returns {String} Um valor de `assetStates`.
 */
export function getAssetState(sensors) {
  const operating = (sensors || []).filter(
    (sensor) => !isNeutralStatus(sensor.status),
  );

  if (!operating.length) {
    return assetStates.OUT_OF_SERVICE;
  }

  if (
    operating.some((sensor) => ATTENTION_STATUSES.indexOf(sensor.status) >= 0)
  ) {
    return assetStates.ATTENTION;
  }

  return assetStates.NORMAL;
}

/**
 * Consolida vários estados em um só, pelo mais grave.
 *
 * @param {String[]} states Estados a consolidar.
 * @returns {String} O estado mais grave, ou Fora de Operação se a lista é vazia.
 */
export function getWorstState(states) {
  const found = assetStatesBySeverity.filter(
    (state) => (states || []).indexOf(state) >= 0,
  );

  return found.length ? found[0] : assetStates.OUT_OF_SERVICE;
}

/**
 * Monta um Ativo a partir dos sensores que compartilham o mesmo `ativoId`.
 *
 * Dois ativos homônimos na mesma instalação têm `ativoId` distintos e viram
 * cartões separados. Um Ativo pode ter dois Sensores do mesmo `moduleId` — o par
 * que nunca se repete é `(ativoId, moduloId)`.
 */
function buildAsset(installation, key, sensors) {
  const orderedSensors = sensors
    .slice()
    .sort((a, b) => compareNames(a.name, b.name));

  return {
    key,
    name: sensors[0].asset,
    company: installation.company,
    companyId: installation.companyId,
    installation: installation.installation,
    installationId: installation.installationId,
    regionalId: installation.regionalId || null,
    regional: installation.regional || null,
    sensors: orderedSensors,
    state: getAssetState(orderedSensors),
    neutralCount: orderedSensors.filter((sensor) => isNeutralStatus(sensor.status)).length,
  };
}

/**
 * Achata a resposta em uma lista de Ativos.
 *
 * O Ativo é a unidade estável do painel: o agrupamento exibido muda
 * (empresa, instalação, ativo), mas a lista abaixo não. Ver `panelGrouping.js`.
 *
 * @param {Object[]} items Instalações vindas de `/StatusRede/listagem`.
 * @returns {Object[]} Ativos, já com estado consolidado.
 */
export function buildAssets(items) {
  const assets = [];

  (items || []).forEach((installation) => {
    const groups = {};
    const order = [];

    (installation.modules || []).forEach((module) => {
      const key = getAssetKey(module);

      if (!groups[key]) {
        groups[key] = [];
        order.push(key);
      }
      groups[key].push(module);
    });

    order.forEach((key) => {
      assets.push(buildAsset(installation, key, groups[key]));
    });
  });

  return assets;
}

/**
 * Totaliza a frota para os contadores do cabeçalho.
 *
 * Opera sobre a lista plana, então os números não mudam quando o usuário troca o
 * agrupamento exibido — trocar a forma de ver não pode alterar o que existe.
 *
 * @param {Object[]} assets Lista devolvida por `buildAssets`.
 * @returns {Object} Contagens por estado, além de totais e de sensores neutros.
 */
export function summarizeAssets(assets) {
  const byState = {};
  assetStatesBySeverity.forEach((state) => {
    byState[state] = 0;
  });

  const installationIds = [];
  let sensorCount = 0;
  let neutralSensorCount = 0;

  (assets || []).forEach((asset) => {
    byState[asset.state] += 1;
    sensorCount += asset.sensors.length;
    neutralSensorCount += asset.neutralCount;

    if (installationIds.indexOf(asset.installationId) < 0) {
      installationIds.push(asset.installationId);
    }
  });

  return {
    byState,
    installationCount: installationIds.length,
    assetCount: (assets || []).length,
    sensorCount,
    neutralSensorCount,
  };
}
